import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import Img from "@insite/mobius/Img";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductBuyNowButton from "@sunSource/Components/ProductBuyNowButton";
import ProductPrice, { ProductPriceStyles } from "@sunSource/Overrides/Components/ProductPrice";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.ownId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    ownId: string;
    product: ProductModel;
}

type Props = OwnProps &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;
interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}
    export interface BrandDetailsImageStyles {
    singleCardContainer?: InjectableCss;
    productTitleStyle?: TypographyPresentationProps;
    singleCardImage?: InjectableCss;
    productNumberStyle?: TypographyPresentationProps;
    productPriceStyles?: ProductPriceStyles;
    requiresQuoteStyles?: TypographyPresentationProps;
    priceWrapperQuoteRequired?: InjectableCss;
    priceWrapper?: InjectableCss;
    quoteWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
}

export const imageStyles: BrandDetailsImageStyles = {
    quoteMessage: {
        text: { weight: "bold", size: "14px" },
        tooltip: {
            iconProps: { size: 14 },
            cssOverrides: {
                tooltipWrapper: css`
                    white-space: normal;
                    z-index: 2;
                `,
                tooltipContainer: css`
                    transform: translateX(-100%);
                `,
            },
        },
    },
    singleCardContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 10px 15px;
            transition: transform 0.3s ease;
            border-radius: 10px;
            box-shadow: 1px 2px 8px hsl(0deg 0% 0% / 0.3);
            width: 250px;
            min-height: 345px;
            :hover {
                transform: scale(1.05);
            }
        `,
    },
    productTitleStyle: {
        css: css`
            max-width: 190px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 13px;
        `,
    },
    singleCardImage: {
        css: css`
            max-height: 110px;
            margin-bottom: 15px;
            max-width: 100%;
        `,
    },
    productNumberStyle: {
        css: css`
            max-width: 190px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            color: rgb(0, 83, 155);
        `,
    },
    quoteWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 26px;
            margin-bottom: 42px;
        `,
    },
    priceWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: center;
            width: 85%;
            text-align: center;
            margin-top: 15px;
            margin-bottom: 45px;
        `,
    },
    priceWrapperQuoteRequired: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: center;
            width: 85%;
            margin-top: 12px;
            text-align: center;
        `,
    },
    productPriceStyles: {
        wrapper: css`
            width: 50%;
        `,
        invalidPriceMessage: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    requiresQuoteStyles: {
        css: css`
            font-size: 13px;
            max-width: 180px;
        `,
    },
};

const styles = imageStyles;
 const quoteStyles = styles.quoteMessage || {};
const BrandDetailsPopularProductsCard: React.FC<Props> = ({
    ownId,
    product,
    updateProductInfo,
    productInfo,
}: Props) => {
    if (!product || !productInfo) {
        return null;
    }

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: ownId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: ownId, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const productPricing = productContext.productInfo.pricing;
    const productListingPrice = productPricing?.unitRegularPrice;
    const productQuoteRequired = productContext.product.quoteRequired;
    const failedToLoadPricing = productContext.productInfo.failedToLoadPricing;
    const allowZeroPricing = productContext.product.allowZeroPricing;
    const isPromotionItem = false;
    const isInvalidPrice =
        !productQuoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        productPricing?.unitNetPrice === 0 &&
        productPricing?.unitRegularPrice === 0;

    const productActionsSiteMessage = siteMessage("ProductActions_SiteMessage");
    const quoteRequiredStyling = failedToLoadPricing || productQuoteRequired || isInvalidPrice;

    return (
        <ProductContext.Provider value={productContext}>
            <Clickable href={product.canonicalUrl}>
                <StyledWrapper {...styles.singleCardContainer}>
                    <Img
                        {...styles.singleCardImage}
                        data-test-selector="productImage"
                        src={product.smallImagePath}
                        altText={product.productNumber}
                    />
                    <Typography {...styles.productNumberStyle}>{product.productNumber}</Typography>
                    <Typography {...styles.productTitleStyle}>{product.productTitle}</Typography>

                    {productListingPrice! < 1 && productQuoteRequired === false ? (
                        <>
                            <StyledWrapper {...styles.quoteWrapper}>
                                <StyledWrapper style={{display:"flex", alignItems:"center", gap:"5px"}}>
                                {translate("Price:")}
                                <StyledWrapper  style={{display:"flex", alignItems:"center", gap:"1px"}}>
                                    <Typography {...quoteStyles.text}>
                                        Requires Quote
                                    </Typography>
                                    <Tooltip
                                        {...quoteStyles.tooltip}
                                        text={siteMessage("Rfq_TooltipMessage").toString()}
                                    />
                                </StyledWrapper>
                                </StyledWrapper>
                                <Typography {...styles.requiresQuoteStyles}>{productActionsSiteMessage}</Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <ProductBuyNowButton />
                            </StyledWrapper>
                        </>
                    ) : (
                        <>
                            <StyledWrapper
                                {...(quoteRequiredStyling ? styles.priceWrapperQuoteRequired : styles.priceWrapper)}
                            >
                                <ProductPrice
                                    showLabel={false}
                                    product={productContext}
                                    extendedStyles={styles.productPriceStyles}
                                />
                            </StyledWrapper>
                            <StyledWrapper>
                                <ProductBuyNowButton />
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            </Clickable>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(BrandDetailsPopularProductsCard));
